import { normalizeAngle, type Micron } from './units';

/**
 * 기하 연산 (작업지시서 P2-01)
 *
 * 모든 좌표는 정수 µm. 삼각함수가 개입하는 결과는 반드시 정수로 되돌린다.
 * 여기 있는 함수들은 순수 함수이므로 단위 테스트로 고정한다.
 */

export interface Pt {
  x: Micron;
  y: Micron;
}

/** 축 정렬 경계 상자 */
export interface Box {
  minX: Micron;
  minY: Micron;
  maxX: Micron;
  maxY: Micron;
}

export const ORIGIN: Pt = { x: 0, y: 0 };

export function pt(x: number, y: number): Pt {
  return { x: Math.round(x), y: Math.round(y) };
}

export function eq(a: Pt, b: Pt): boolean {
  return a.x === b.x && a.y === b.y;
}

export function add(a: Pt, b: Pt): Pt {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function sub(a: Pt, b: Pt): Pt {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(a: Pt, k: number): Pt {
  return pt(a.x * k, a.y * k);
}

export function dot(a: Pt, b: Pt): number {
  return a.x * b.x + a.y * b.y;
}

export function cross(a: Pt, b: Pt): number {
  return a.x * b.y - a.y * b.x;
}

/** 거리. 실수를 반환한다(비교·표시용). 좌표로 쓰려면 반올림할 것. */
export function dist(a: Pt, b: Pt): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function distSq(a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return dx * dx + dy * dy;
}

export function len(v: Pt): number {
  return Math.hypot(v.x, v.y);
}

/** 단위 벡터. 길이 0이면 (0,0). 정수화하지 않는다(방향 계산 중간값). */
export function unit(v: Pt): { x: number; y: number } {
  const l = Math.hypot(v.x, v.y);
  if (l === 0) return { x: 0, y: 0 };
  return { x: v.x / l, y: v.y / l };
}

/** a → b 방향 각도 (라디안, 0 ≤ θ < 2π) */
export function angleOf(a: Pt, b: Pt): number {
  return normalizeAngle(Math.atan2(b.y - a.y, b.x - a.x));
}

/** 기준점에서 각도·거리만큼 떨어진 점 (극좌표) */
export function polar(base: Pt, angleRad: number, distance: number): Pt {
  return pt(base.x + Math.cos(angleRad) * distance, base.y + Math.sin(angleRad) * distance);
}

/** 점을 중심 기준으로 회전 */
export function rotate(p: Pt, center: Pt, angleRad: number): Pt {
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  return pt(center.x + dx * c - dy * s, center.y + dx * s + dy * c);
}

/** 점을 중심 기준으로 확대/축소 */
export function scaleAbout(p: Pt, center: Pt, k: number): Pt {
  return pt(center.x + (p.x - center.x) * k, center.y + (p.y - center.y) * k);
}

/** 두 점을 지나는 직선에 대해 반사 */
export function mirror(p: Pt, a: Pt, b: Pt): Pt {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const denom = dx * dx + dy * dy;
  if (denom === 0) return { ...p };
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / denom;
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return pt(2 * projX - p.x, 2 * projY - p.y);
}

/** 중점 */
export function midpoint(a: Pt, b: Pt): Pt {
  return pt((a.x + b.x) / 2, (a.y + b.y) / 2);
}

/* -------------------------------------------------------------------------- *
 * 선분
 * -------------------------------------------------------------------------- */

/**
 * 점에서 선분까지의 최근접점.
 * `clamp = false` 면 무한 직선으로 취급한다 (EXTEND, 수직 스냅에서 필요).
 */
export function closestPointOnSegment(p: Pt, a: Pt, b: Pt, clamp = true): Pt {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const denom = dx * dx + dy * dy;
  if (denom === 0) return { ...a };
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / denom;
  if (clamp) t = Math.max(0, Math.min(1, t));
  return pt(a.x + t * dx, a.y + t * dy);
}

/** 점 → 선분 거리 */
export function distToSegment(p: Pt, a: Pt, b: Pt): number {
  return dist(p, closestPointOnSegment(p, a, b));
}

/**
 * 두 선분의 교점.
 * `segment = true` → 두 선분 범위 안에서만. `false` → 무한 직선 교점(TRIM/EXTEND 용).
 * 평행이면 null.
 */
export function intersectLines(
  a1: Pt,
  a2: Pt,
  b1: Pt,
  b2: Pt,
  segment = true
): Pt | null {
  const d1x = a2.x - a1.x;
  const d1y = a2.y - a1.y;
  const d2x = b2.x - b1.x;
  const d2y = b2.y - b1.y;

  const denom = d1x * d2y - d1y * d2x;
  if (denom === 0) return null; // 평행 또는 동일선상

  const t = ((b1.x - a1.x) * d2y - (b1.y - a1.y) * d2x) / denom;
  const u = ((b1.x - a1.x) * d1y - (b1.y - a1.y) * d1x) / denom;

  if (segment) {
    const eps = 1e-9;
    if (t < -eps || t > 1 + eps || u < -eps || u > 1 + eps) return null;
  }
  return pt(a1.x + t * d1x, a1.y + t * d1y);
}

/** 선분과 원의 교점 (0~2개) */
export function intersectSegmentCircle(
  a: Pt,
  b: Pt,
  center: Pt,
  radius: number,
  segment = true
): Pt[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const fx = a.x - center.x;
  const fy = a.y - center.y;

  const A = dx * dx + dy * dy;
  if (A === 0) return [];
  const B = 2 * (fx * dx + fy * dy);
  const C = fx * fx + fy * fy - radius * radius;

  const disc = B * B - 4 * A * C;
  if (disc < 0) return [];

  const sq = Math.sqrt(disc);
  const ts = [(-B - sq) / (2 * A), (-B + sq) / (2 * A)];
  const eps = 1e-9;

  const out: Pt[] = [];
  for (const t of ts) {
    if (segment && (t < -eps || t > 1 + eps)) continue;
    out.push(pt(a.x + t * dx, a.y + t * dy));
  }
  // 접점이면 두 해가 같다 — 중복 제거
  if (out.length === 2 && eq(out[0]!, out[1]!)) out.pop();
  return out;
}

/* -------------------------------------------------------------------------- *
 * 경계 상자
 * -------------------------------------------------------------------------- */

export function boxOf(points: readonly Pt[]): Box {
  if (points.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

export function boxUnion(a: Box, b: Box): Box {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

export function boxExpand(b: Box, by: Micron): Box {
  return { minX: b.minX - by, minY: b.minY - by, maxX: b.maxX + by, maxY: b.maxY + by };
}

export function boxContainsPoint(b: Box, p: Pt): boolean {
  return p.x >= b.minX && p.x <= b.maxX && p.y >= b.minY && p.y <= b.maxY;
}

/** a 가 b 를 완전히 포함하는가 (Window 선택 판정) */
export function boxContainsBox(a: Box, b: Box): boolean {
  return a.minX <= b.minX && a.minY <= b.minY && a.maxX >= b.maxX && a.maxY >= b.maxY;
}

/** 두 상자가 겹치는가 (Crossing 선택 판정) */
export function boxIntersects(a: Box, b: Box): boolean {
  return !(a.maxX < b.minX || a.minX > b.maxX || a.maxY < b.minY || a.minY > b.maxY);
}

export function boxCenter(b: Box): Pt {
  return pt((b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2);
}

export function boxFromPoints(a: Pt, b: Pt): Box {
  return {
    minX: Math.min(a.x, b.x),
    minY: Math.min(a.y, b.y),
    maxX: Math.max(a.x, b.x),
    maxY: Math.max(a.y, b.y),
  };
}

/** 선분이 상자와 교차하는가 (Crossing 선택에서 선분 판정에 필요) */
export function segmentIntersectsBox(a: Pt, b: Pt, box: Box): boolean {
  if (boxContainsPoint(box, a) || boxContainsPoint(box, b)) return true;
  const c1 = { x: box.minX, y: box.minY };
  const c2 = { x: box.maxX, y: box.minY };
  const c3 = { x: box.maxX, y: box.maxY };
  const c4 = { x: box.minX, y: box.maxY };
  return (
    intersectLines(a, b, c1, c2) !== null ||
    intersectLines(a, b, c2, c3) !== null ||
    intersectLines(a, b, c3, c4) !== null ||
    intersectLines(a, b, c4, c1) !== null
  );
}

/* -------------------------------------------------------------------------- *
 * 폴리곤
 * -------------------------------------------------------------------------- */

/** 부호 있는 면적 ×2. 양수면 반시계 방향(CCW). */
export function signedArea2(points: readonly Pt[]): number {
  let s = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    s += a.x * b.y - b.x * a.y;
  }
  return s;
}

/** 폴리곤 면적 (µm²). 항상 양수. */
export function polygonArea(points: readonly Pt[]): number {
  return Math.abs(signedArea2(points)) / 2;
}

/** 폴리라인 길이 */
export function polylineLength(points: readonly Pt[], closed = false): number {
  let total = 0;
  for (let i = 0; i + 1 < points.length; i++) {
    total += dist(points[i]!, points[i + 1]!);
  }
  if (closed && points.length > 2) total += dist(points[points.length - 1]!, points[0]!);
  return total;
}

/** 점이 폴리곤 내부인가 (ray casting) */
export function pointInPolygon(p: Pt, poly: readonly Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

/* -------------------------------------------------------------------------- *
 * 호(Arc)
 * -------------------------------------------------------------------------- */

/** 각도가 호의 범위(start → end, 반시계) 안에 있는가 */
export function angleInArc(angle: number, start: number, end: number): boolean {
  const a = normalizeAngle(angle);
  const s = normalizeAngle(start);
  const e = normalizeAngle(end);
  return s <= e ? a >= s - 1e-9 && a <= e + 1e-9 : a >= s - 1e-9 || a <= e + 1e-9;
}

/** 호 위의 점 */
export function arcPoint(center: Pt, radius: number, angle: number): Pt {
  return polar(center, angle, radius);
}

/**
 * 호의 경계 상자.
 * 단순히 양 끝점만 쓰면 안 된다 — 0°/90°/180°/270° 축 극점이 호 안에 있으면
 * 그 점이 실제 경계가 된다.
 */
export function arcBox(center: Pt, radius: number, start: number, end: number): Box {
  const pts: Pt[] = [arcPoint(center, radius, start), arcPoint(center, radius, end)];
  for (const q of [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2]) {
    if (angleInArc(q, start, end)) pts.push(arcPoint(center, radius, q));
  }
  return boxOf(pts);
}

/**
 * 3차 베지어를 선분 열로 근사 (PDF 벡터 임포트용). tolerance 는 µm 단위 최대 오차.
 * De Casteljau 재귀 이분— 제어점 p1/p2 가 시작-끝 직선에서 tolerance 이내로
 * 들어올 때까지 반으로 쪼갠다. flattenArc 와 같은 tolerance 파라미터 관례를 따른다.
 */
export function flattenCubicBezier(
  p0: Pt,
  p1: Pt,
  p2: Pt,
  p3: Pt,
  tolerance = 200,
  depth = 0
): Pt[] {
  // depth 10 = 세그먼트 최대 1024개(2^10). tolerance 가 극단적으로 좁아도
  // (예: 서브µm) 커브 하나가 수백만 점으로 폭발하지 않게 막는다 — 실사용
  // 축척에서 1024 분할이면 이미 육안으로 완전히 매끄럽다.
  if (depth >= 10 || isBezierFlat(p0, p1, p2, p3, tolerance)) {
    return [p0, p3];
  }
  const { left, right } = subdivideBezier(p0, p1, p2, p3);
  const a = flattenCubicBezier(left[0], left[1], left[2], left[3], tolerance, depth + 1);
  const b = flattenCubicBezier(right[0], right[1], right[2], right[3], tolerance, depth + 1);
  return [...a, ...b.slice(1)];
}

function isBezierFlat(p0: Pt, p1: Pt, p2: Pt, p3: Pt, tolerance: number): boolean {
  const d1 = dist(p1, closestPointOnSegment(p1, p0, p3, false));
  const d2 = dist(p2, closestPointOnSegment(p2, p0, p3, false));
  return d1 <= tolerance && d2 <= tolerance;
}

function mid(a: Pt, b: Pt): Pt {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function subdivideBezier(
  p0: Pt,
  p1: Pt,
  p2: Pt,
  p3: Pt
): { left: [Pt, Pt, Pt, Pt]; right: [Pt, Pt, Pt, Pt] } {
  const p01 = mid(p0, p1);
  const p12 = mid(p1, p2);
  const p23 = mid(p2, p3);
  const p012 = mid(p01, p12);
  const p123 = mid(p12, p23);
  const p0123 = mid(p012, p123);
  return {
    left: [p0, p01, p012, p0123],
    right: [p0123, p123, p23, p3],
  };
}

/** 호를 선분 열로 근사 (렌더링·교차 계산용). tolerance 는 µm 단위 최대 오차. */
export function flattenArc(
  center: Pt,
  radius: number,
  start: number,
  end: number,
  tolerance = 200
): Pt[] {
  const s = normalizeAngle(start);
  let sweep = normalizeAngle(end) - s;
  if (sweep <= 0) sweep += Math.PI * 2;

  // 현-호 오차가 tolerance 이하가 되는 분할 수
  const maxStep = radius > tolerance ? 2 * Math.acos(1 - tolerance / radius) : Math.PI / 2;
  const steps = Math.max(2, Math.ceil(sweep / maxStep));

  const out: Pt[] = [];
  for (let i = 0; i <= steps; i++) {
    out.push(arcPoint(center, radius, s + (sweep * i) / steps));
  }
  return out;
}
